import createModule from "../createModule"
import view from "./view"
import { Creators as ProfileCreators } from "model/profileModal"
import { Creators as CompositeCreators } from "modules/analyze-composite-security/model"
import { Creators } from "./model"
import { getSortedContributors, getSelectedGroupValue, getAvailableRollups, getSortedGroupContributors, getContributorsSummaryText, getCsvData, getGroupBy, getIncludeNotHeld, getSelectedGroupSiblings, getSelectedGroup } from "./selectors"
import { getGroupsDropdownColumns } from "./selectors/group"
import { generateClassificationQueryForContributors } from "helpers/classification"
import { getQueryGroupBy } from "helpers/rollups"
import { VIEW_TYPES } from "helpers/base"
import { convertPositionSetType } from "helpers/compositeSecurities"
import { getPage } from "helpers/compositeSecuritiesDetails"
import { getSelectedAccount } from "modules/account-root/selectors"

export default createModule({
  view,
  externalProps: (state, props) => ({
    account: getSelectedAccount(state),
    includeNotHeld: getIncludeNotHeld(state, props),
  }),
  required: ["account", "positionSetId"],
  data: [{
    resource: "compositePerformanceContributors",
    as: null,
    url: ({ modelId, positionSetId, positionSetType, selectedDate, viewType, expand, base, baseSignature, performanceFetchStartDate, segment, classifications, includeNotHeld }) => {
      return {
        id: `compositePerformanceContributors-${modelId}${positionSetId}${positionSetType}${performanceFetchStartDate}${selectedDate}${viewType}${expand}${baseSignature}${JSON.stringify(segment)}${includeNotHeld}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput! $from: Date!, $to: Date!, $base: [PositionSetInput], $includeNotHeld: Boolean) {
                model (id: $modelId) {
                  simulation (positionSet: $positionSet, from: $from, to: $to, base: $base, interval: AUTO) {
                    performanceContributors(includeNotHeld: $includeNotHeld) {
                      id
                      modelProviderId
                      assetClass
                      assetSubclass
                      averagePercentEquity
                      totalReturn {
                        cumulativePercent
                      }
                      country
                      currency
                      ${generateClassificationQueryForContributors("GICS", classifications)}
                      notHeld
                      description
                      bloombergTicker
                      attribution {
                        summary {
                          factorsReturn {
                            cumulativePercent
                          }
                          specificReturn {
                            cumulativePercent
                          }
                          tradingReturn {
                            cumulativePercent
                          }
                        }
                        factors {
                          id
                          category
                          return {
                            cumulativePercent
                          }
                        }
                      }
                    }
                  }
                }
              }`,
        variables: {
          modelId,
          positionSet: {
            id: positionSetId,
            type: convertPositionSetType(positionSetType),
            segment,
            expand,
          },
          from: performanceFetchStartDate,
          to: selectedDate,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          includeNotHeld,
        },
      }
    },
  }, {
    resource: "compositeGroupedPerformanceContributors",
    as: null,
    url: ({ positionSetId, positionSetType, modelId, viewType, expand, selectedDate, currentGrouping, performanceFetchStartDate, baseSignature, base, segment, classifications, includeNotHeld }) => {
      if (!currentGrouping) {
        return null
      }

      const queryGroupBy = getQueryGroupBy(currentGrouping, viewType === VIEW_TYPES.ACTIVE)

      return {
        id: `compositeGroupedPerformanceContributors-${modelId}${positionSetId}${positionSetType}${performanceFetchStartDate}${selectedDate}${viewType}${expand}${baseSignature}${JSON.stringify(queryGroupBy)}${JSON.stringify(segment)}${includeNotHeld}`,
        query: `query ($modelId: String!, $positionSet: PositionSetInput!, $from: Date!, $to: Date!, $base: [PositionSetInput], $groupBy: ContributorGroupType!, $groupById: String, $tier: String, $includeNotHeld: Boolean) {
              model (id: $modelId) {
                simulation (positionSet: $positionSet, from: $from, to: $to, base: $base, interval: AUTO) {
                  groupedPerformanceContributors (groupBy: $groupBy, groupById: $groupById, classificationTier: $tier, includeNotHeld: $includeNotHeld) {
                    name
                    id
                    totalReturn {
                      cumulativePercent
                    }
                    averagePercentEquity
                    attribution {
                      summary {
                        factorsReturn {
                          cumulativePercent
                        }
                        specificReturn {
                          cumulativePercent
                        }
                        tradingReturn {
                          cumulativePercent
                        }
                      }
                      factors {
                        id
                        category
                        return {
                          cumulativePercent
                        }
                      }
                    }
                    contributors {
                      id
                      assetClass
                      country
                      notHeld
                      ${generateClassificationQueryForContributors("GICS", classifications)}
                      currency
                      description
                      averagePercentEquity
                      totalReturn {
                        cumulativePercent
                      }
                      bloombergTicker
                      modelProviderId
                      attribution {
                        summary {
                          factorsReturn {
                            cumulativePercent
                          }
                          specificReturn {
                            cumulativePercent
                          }
                          tradingReturn {
                            cumulativePercent
                          }
                        }
                        factors {
                          id
                          category
                          return {
                            cumulativePercent
                          }
                        }
                      }
                    }
                  }
                }
              }
            }`,
        variables: {
          modelId,
          positionSet: {
            id: positionSetId,
            type: convertPositionSetType(positionSetType),
            segment,
            expand,
          },
          from: performanceFetchStartDate,
          to: selectedDate,
          base: viewType === VIEW_TYPES.ACTIVE ? [base] : undefined,
          includeNotHeld,
          ...queryGroupBy,
        },
      }
    },
  }],
  mapStateToProps: (state, props) => ({
    data: getSortedContributors(state, props),
    groupedData: getSortedGroupContributors(state, props),
    availableRollups: getAvailableRollups(state, props),
    contributorsSummaryText: getContributorsSummaryText(state, props),
    excelData: getCsvData(state, props),
    selectedGroupSiblings: getSelectedGroupSiblings(state, props),
    selectedGroup: getSelectedGroup(state, props),
    selectedGroupValue: getSelectedGroupValue(state, props),
    groupBy: getGroupBy(state, props),
    groupsDropdownColumns: getGroupsDropdownColumns(state, props),
    page: getPage(state),
  }),
  actions: {
    ...Creators,
    openSecurityProfile: ProfileCreators.openSecurityProfile,
    openFactorProfile: ProfileCreators.openFactorProfile,
    openCompositeSecurity: CompositeCreators.openCompositeSecurity,
  },
})
